import type { ApiClientDocument } from '@myplayer/schema';
import { IconCheck, IconKey, IconPlus, IconShieldOff } from '@myplayer/ui';
import { useEffect, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { useAuth } from './auth-context.js';
import { formatWorkspaceName } from './auth-session.js';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardIcon,
  CardTitle,
  LinearSpinner,
  Select,
  Tag,
  useContextMenu,
} from './components/ui/primitives.js';
import { buildApiKeyContextMenuItems } from './context-menu-items.js';
import {
  callCreateApiClientFunction,
  callRevokeApiClientFunction,
  getFirebaseClients,
  type CreateApiClientCallablePayload,
  type CreateApiClientCallableResult,
  type PartnerApiScope,
  type RevokeApiClientCallablePayload,
  type RevokeApiClientCallableResult,
} from './firebase.js';
import { formatCapabilityRestrictionNotice } from './rbac-ui.js';
import { ADMIN_STRINGS, formatPlainAdminError } from './strings.js';
import { CreateApiKeyModal } from './settings-modals.js';
import {
  formatScopeLabel,
  mapSnapshotToApiClients,
  validateApiKeyDraft,
} from './workspaces-firestore.js';

export { AVAILABLE_PARTNER_API_SCOPES } from './workspaces-firestore.js';

export interface ApiKeysSectionProps {
  readonly initialApiClients?: readonly ApiClientDocument[];
  readonly showReadOnlyNoticeWhenUnauthorized?: boolean;
  readonly onCreateApiClient?: (
    payload: CreateApiClientCallablePayload,
  ) => Promise<CreateApiClientCallableResult>;
  readonly onRevokeApiClient?: (
    payload: RevokeApiClientCallablePayload,
  ) => Promise<RevokeApiClientCallableResult>;
}

export function ApiKeysSection({
  initialApiClients,
  showReadOnlyNoticeWhenUnauthorized = false,
  onCreateApiClient,
  onRevokeApiClient,
}: ApiKeysSectionProps): ReactElement | null {
  const { session, can, availableWorkspaces } = useAuth();
  const { bindContextMenu, contextMenuElement } = useContextMenu();
  const orgId = session?.orgId ?? '';
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(session?.activeWorkspaceId ?? '');
  const workspaceId = selectedWorkspaceId || session?.activeWorkspaceId || '';
  const workspaceName =
    availableWorkspaces.find((ws) => ws.workspaceId === workspaceId)?.name ??
    formatWorkspaceName(undefined, workspaceId);
  const canReadApiClients = can('apiClient:read');
  const canManageApiClients = can('apiClient:manage_callable');

  const [apiClients, setApiClients] = useState<readonly ApiClientDocument[]>(
    initialApiClients ?? [],
  );
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [rateLimitRpm, setRateLimitRpm] = useState('600');
  const [selectedScopes, setSelectedScopes] = useState<readonly PartnerApiScope[]>([
    'identifiers:read',
    'identifiers:write',
  ]);
  const [createdRawKey, setCreatedRawKey] = useState<string | null>(null);
  const [copiedRawKey, setCopiedRawKey] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (initialApiClients !== undefined) {
      setApiClients(initialApiClients);
      return;
    }
    if (!orgId || !workspaceId || !canReadApiClients) return;
    let unsubscribe: (() => void) | undefined;
    let active = true;
    void (async () => {
      try {
        const { collection, onSnapshot } = await import('firebase/firestore');
        if (!active) return;
        const { db } = getFirebaseClients();
        const colRef = collection(
          db,
          'organisations',
          orgId,
          'workspaces',
          workspaceId,
          'apiClients',
        );
        unsubscribe = onSnapshot(
          colRef,
          (snap) => setApiClients(mapSnapshotToApiClients(snap.docs, orgId, workspaceId)),
          (err) =>
            setErrorMessage(formatPlainAdminError(err, ADMIN_STRINGS.errors.apiKeysActionFallback)),
        );
      } catch {
        // Ignore in non-Firestore test environments
      }
    })();
    return () => {
      active = false;
      if (unsubscribe) unsubscribe();
    };
  }, [orgId, workspaceId, canReadApiClients, initialApiClients]);

  if (!canReadApiClients && !canManageApiClients) {
    if (!showReadOnlyNoticeWhenUnauthorized) return null;
    const notice = formatCapabilityRestrictionNotice({
      capability: 'apiClient:read',
      actionSummary: 'view, create, or revoke Partner API keys',
      callerRole: session?.personaRole,
    });
    return (
      <Card className="h-full" data-testid="api-keys-card">
        <CardHeader>
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <CardIcon>
              <IconKey />
            </CardIcon>
            <CardTitle>Partner API Keys</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p
            data-testid="api-keys-readonly-notice"
            className="rounded-control border border-subtle bg-surface-subtle px-3 py-2 text-[12px] text-fg-secondary"
          >
            {notice}
          </p>
        </CardContent>
      </Card>
    );
  }

  const createFn = onCreateApiClient ?? callCreateApiClientFunction;
  const revokeFn = onRevokeApiClient ?? callRevokeApiClientFunction;

  const toggleScope = (scope: PartnerApiScope): void => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  };

  const handleCreateKey = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canManageApiClients || submitting || submittingRef.current) return;
    const validation = validateApiKeyDraft(keyName, selectedScopes, rateLimitRpm);
    if (!validation.ok) {
      setErrorMessage(validation.error);
      return;
    }
    const { trimmedName, parsedRpm } = validation;
    setErrorMessage(null);
    setCreatedRawKey(null);
    setCopiedRawKey(false);
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const payload = {
        orgId,
        workspaceId,
        name: trimmedName,
        scopes: selectedScopes,
        rateLimitRpm: parsedRpm,
      };
      const result = await createFn(payload);
      setCreatedRawKey(result.rawApiKey);
      const newMapped = mapSnapshotToApiClients(
        [{ id: result.clientId, data: () => ({ ...result, ...payload }) }],
        orgId,
        workspaceId,
      );
      setApiClients((prev) => [
        ...prev.filter((item) => item.clientId !== result.clientId),
        ...newMapped,
      ]);
      setKeyName('');
      setCreateModalOpen(false);
    } catch (err: unknown) {
      setErrorMessage(formatPlainAdminError(err, ADMIN_STRINGS.errors.apiKeysActionFallback));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleRevokeKey = async (clientId: string): Promise<void> => {
    if (!canManageApiClients) return;
    setErrorMessage(null);
    setSubmitting(true);
    try {
      const res = await revokeFn({ orgId, workspaceId, clientId });
      setApiClients((prev) =>
        prev.map((item) =>
          item.clientId === clientId
            ? { ...item, status: res.status, updatedAt: res.revokedAt }
            : item,
        ),
      );
    } catch (err: unknown) {
      setErrorMessage(formatPlainAdminError(err, ADMIN_STRINGS.errors.apiKeysActionFallback));
    } finally {
      setSubmitting(false);
    }
  };

  const closeCreateModal = (): void => {
    setCreateModalOpen(false);
    setErrorMessage(null);
  };

  const statusVariant = (s: string) =>
    s === 'ACTIVE' ? 'success' : s === 'REVOKED' ? 'destructive' : 'secondary';
  const statusLabel = (s: string) => (s === 'ACTIVE' ? 'Active' : s === 'REVOKED' ? 'Revoked' : s);

  return (
    <Card className="h-full" data-testid="api-keys-card">
      {submitting && <LinearSpinner />}
      <CardHeader>
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <CardIcon>
            <IconKey />
          </CardIcon>
          <CardTitle>Partner API Keys</CardTitle>
          <Tag variant="outline" size="sm">
            {apiClients.filter((c) => c.status === 'ACTIVE').length} active
          </Tag>
        </div>
        {canManageApiClients && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={submitting}
            className="gap-1.5"
            data-testid="open-create-api-key-btn"
            onClick={() => {
              setKeyName('');
              setRateLimitRpm('600');
              setSelectedScopes(['identifiers:read', 'identifiers:write']);
              setShowAdvanced(false);
              setErrorMessage(null);
              setCreateModalOpen(true);
            }}
          >
            <IconPlus />
            <span>Create API Key</span>
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardDescription>
            Create and revoke Partner API keys for{' '}
            <span className="font-medium text-fg">{workspaceName}</span>
          </CardDescription>
          {availableWorkspaces.length > 1 && (
            <Select
              aria-label="Filter API keys by workspace"
              data-testid="api-keys-workspace-filter"
              value={workspaceId}
              onChange={(e) => setSelectedWorkspaceId(e.target.value)}
            >
              {availableWorkspaces.map((ws) => (
                <option key={ws.workspaceId} value={ws.workspaceId}>
                  {ws.name}
                </option>
              ))}
            </Select>
          )}
        </div>
        {createdRawKey !== null && (
          <div
            role="status"
            aria-label="New API key secret"
            className="rounded-control border border-brand bg-brand-wash p-3 text-[12px] text-fg"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-semibold">
                Copy your new Partner API key now — it will never be shown again:
              </p>
              <Button
                type="button"
                variant="secondary"
                size="xs"
                data-testid="copy-api-key-secret-btn"
                aria-label="Copy API key"
                onClick={() => {
                  void navigator.clipboard?.writeText(createdRawKey);
                  setCopiedRawKey(true);
                  setTimeout(() => setCopiedRawKey(false), 2000);
                }}
                className="shrink-0 gap-1"
              >
                {copiedRawKey && <IconCheck />}
                <span>{copiedRawKey ? 'Copied' : 'Copy'}</span>
              </Button>
            </div>
            <code className="mt-1.5 block break-all rounded-control border border-border bg-surface p-2 font-mono text-[12px] text-fg">
              {createdRawKey}
            </code>
          </div>
        )}
        {errorMessage !== null && !createModalOpen && (
          <p
            role="alert"
            className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] font-medium text-status-error"
          >
            {errorMessage}
          </p>
        )}
        <div className="divide-y divide-subtle rounded-control border border-subtle">
          {apiClients.length === 0 ? (
            <p className="px-3.5 py-6 text-center text-[13px] text-fg-secondary">
              {ADMIN_STRINGS.empty.apiKeys}
            </p>
          ) : (
            apiClients.map((client) => (
              <div
                key={client.clientId}
                data-testid={`api-key-row-${client.clientId}`}
                onContextMenu={bindContextMenu(() =>
                  buildApiKeyContextMenuItems({
                    client,
                    canManageApiClients,
                    submitting,
                    onRevoke: (clientId) => void handleRevokeKey(clientId),
                  }),
                )}
                className="flex min-h-12 flex-col gap-2 px-3.5 py-3 transition-colors hover:bg-surface-muted md:flex-row md:items-center md:justify-between"
              >
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[14px] font-medium text-fg">{client.name}</span>
                    <Badge variant={statusVariant(client.status)}>
                      {statusLabel(client.status)}
                    </Badge>
                    <Tag variant="mono" size="sm">
                      {client.keyPrefix}
                    </Tag>
                    <Tag variant="outline" size="sm">
                      {client.rateLimitRpm}/min
                    </Tag>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-fg-secondary">
                    <span>Permissions:</span>
                    {client.scopes.map((scope) => (
                      <Tag key={scope} variant="outline" size="sm">
                        {formatScopeLabel(scope)}
                      </Tag>
                    ))}
                  </div>
                </div>
                {canManageApiClients && client.status === 'ACTIVE' && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="xs"
                    disabled={submitting}
                    aria-label={`Revoke ${client.name}`}
                    onClick={() => void handleRevokeKey(client.clientId)}
                    className="shrink-0"
                  >
                    <IconShieldOff />
                    <span>Revoke Key</span>
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
        {contextMenuElement}
      </CardContent>

      <CreateApiKeyModal
        open={createModalOpen}
        workspaceId={workspaceId}
        workspaceName={workspaceName}
        availableWorkspaces={availableWorkspaces}
        onWorkspaceChange={setSelectedWorkspaceId}
        keyName={keyName}
        setKeyName={setKeyName}
        rateLimitRpm={rateLimitRpm}
        setRateLimitRpm={setRateLimitRpm}
        selectedScopes={selectedScopes}
        toggleScope={toggleScope}
        showAdvanced={showAdvanced}
        setShowAdvanced={setShowAdvanced}
        submitting={submitting}
        errorMessage={errorMessage}
        onClose={closeCreateModal}
        onSubmit={(e) => void handleCreateKey(e)}
      />
    </Card>
  );
}
